import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/internal/Observable';
import {catchError, flatMap, map, tap} from 'rxjs/operators';
import * as UrlAssembler from 'url-assembler';
import {environment} from '../../../environments/environment';
import {CachingInterceptor} from '../../caching/services/caching-interceptor';
import {Attribute, AttributeInputType, AttributeOption} from '../models/attribute';
import {productsErrorSelector} from './products-error-selector';

const ProductsResources = {
    productsAttributeSets: '/V1/products/attribute-sets/sets/list',
    productsAttributeSetAttributes: '/V1/products/attribute-sets/:attributeSetId/extendedAttributes',
};

@Injectable()
export class ProductsAttributesApiService {
    private productAttributeSetsUrlAssembler;
    private productsAttributeSetAttributesUrlAssembler;

    private static convertAttributeEntity(attributeEntity: AttributeEntity): Attribute {
        return {
            code: attributeEntity.attribute_code,
            label: attributeEntity.default_frontend_label,
            inputType: this.determineAttributeInputType(attributeEntity),
            isSearchable: attributeEntity.is_searchable === '1',
            isVisible: attributeEntity.is_visible,
            options: attributeEntity.options
        };
    }

    private static determineAttributeInputType(attributeEntity: AttributeEntity): AttributeInputType {
        switch (attributeEntity.frontend_input) {
            case 'select':
            case 'multiselect':
                return AttributeInputType.SELECT;
            case 'text':
            case 'textarea':
                return AttributeInputType.TEXT;
            case 'boolean':
                return AttributeInputType.BOOLEAN;
            case 'price':
                return AttributeInputType.PRICE;
            default:
                console.log('unknown attribute frontend input type. Use text instead. - (code input_type):',
                    attributeEntity.attribute_code, attributeEntity.frontend_input);
                return AttributeInputType.TEXT;
        }
    }

    constructor(private http: HttpClient) {
        const base = UrlAssembler(environment.rest_backend);

        this.productAttributeSetsUrlAssembler = base.segment(ProductsResources.productsAttributeSets);
        this.productsAttributeSetAttributesUrlAssembler = base.segment(ProductsResources.productsAttributeSetAttributes);
    }

    getAttributesOfSet(name: string): Observable<Attribute[]> {
        return this.getAttributeSet(name).pipe(
            tap((set: AttributeSetEntity) => console.log('load attributes from attribute set', set)),
            flatMap((set: AttributeSetEntity) => this.getAttributes(set.attribute_set_id))
        );
    }

    private getAttributeSet(name: string): Observable<AttributeSetEntity> {
        return this.http.get<AttributeSetsEntity>(this.determineProductAttributeSetsUrl(name), {
            headers: CachingInterceptor.createCacheHeader()
        }).pipe(
            tap(e => console.log('found attribute sets', e)),
            map((e: AttributeSetsEntity) => e.items[0]),
            catchError(productsErrorSelector)
        );
    }

    private getAttributes(attributeSetId: number): Observable<Attribute[]> {
        return this.http.get<AttributeEntity[]>(this.determineProductAttributesUrl(attributeSetId), {
            headers: CachingInterceptor.createCacheHeader()
        }).pipe(
            tap((entities: AttributeEntity[]) => console.log('found attribute entities', entities)),
            map((entities: AttributeEntity[]) =>
                entities.map(entity => ProductsAttributesApiService.convertAttributeEntity(entity))),
            catchError(productsErrorSelector)
        );
    }

    private determineProductAttributeSetsUrl(name: string): string {
        return this.productAttributeSetsUrlAssembler.query({
            'searchCriteria[page_size]': 1,
            'searchCriteria[current_page]': 1,
            'searchCriteria[filter_groups][0][filters][0][field]': 'attribute_set_name',
            'searchCriteria[filter_groups][0][filters][0][value]': name,
            'searchCriteria[filter_groups][0][filters][0][condition_type]': 'eq'
        }).toString();
    }

    private determineProductAttributesUrl(attributeSetId: number): string {
        return this.productsAttributeSetAttributesUrlAssembler
            .param({
                'attributeSetId': attributeSetId.toString()
            })
            .toString();
    }
}

interface AttributeSetsEntity {
    items: AttributeSetEntity[];
    total_count: number;
}

interface AttributeSetEntity {
    attribute_set_id: number;
    attribute_set_name: string;
    sort_order: number;
    entity_type_id: number;
}

interface AttributeEntity {
    is_wysiwyg_enabled: boolean;
    is_html_allowed_on_front: boolean;
    used_for_sort_by: boolean;
    is_filterable: boolean;
    is_filterable_in_search: boolean;
    is_used_in_grid: boolean;
    is_visible_in_grid: boolean;
    is_filterable_in_grid: boolean;
    position: number;
    apply_to: string[];
    is_searchable: string;
    is_visible_in_advanced_search: string;
    is_comparable: string;
    is_used_for_promo_rules: string;
    is_visible_on_front: string;
    used_in_product_listing: string;
    is_visible: boolean;
    scope: string;
    attribute_id: number;
    attribute_code: string;
    frontend_input: string;
    entity_type_id: number;
    is_required: boolean;
    options: AttributeOption[];
    is_user_defined: boolean;
    default_frontend_label: string;
    frontend_labels: string;
    backend_type: string;
    source_model: string;
    is_unique: number;
    // validation_rules; // Type of validation_rules unknown yet, it is empty always!?
}
